import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import getConfig from 'next/config';

import { PUSH_ACTIVITY_TYPES, USER_ACTIVITY_EVENTS } from 'utils/constant';

import { logActivity } from './logActivity';
import Service from './service';
import { updateUserRating } from './userService';

const {
  publicRuntimeConfig: {
    HttpRoutes,
    PUSH_NOTIFICATIONS_VAPID_KEY,
    PUSH_SUBSCRIPTION_POLLING_INTERVAL,
  },
} = getConfig();

export const currentSubscriptionLocalStorageKey = 'currentPushSubscription';

/**
 * This function is used for polling by some interval (ex: every 4 hours) to check whether the old push subscription has lost/malformed/expired.
 * If the old push subscription has lost/malformed/expired then this method will unsubscribe it and subscription a new subscription from PushManager and
 * the same is sent to Push Server
 * @param {Object} user
 * @returns
 */
const pollForPushSubscriptionChange = async (user = {}) => {
  const userLoggedIn = user && user.isLoggedIn;
  const userId = user?.userId;

  if (!userId) {
    return;
  }

  try {
    let oldSubscriptionUnparsed = window.localStorage.getItem(currentSubscriptionLocalStorageKey);
    let oldSubscription = {};
    if (oldSubscriptionUnparsed) {
      oldSubscription = JSON.parse(oldSubscriptionUnparsed);
    }

    if (!_isEmpty(oldSubscription)) {
      let newSubscription = await getSubscription();

      if (newSubscription) {
        newSubscription = newSubscription.toJSON();
      }

      // Calculate the diff of old and new subscription endpoints
      if (
        oldSubscription?.endpoint &&
        newSubscription?.endpoint &&
        oldSubscription?.endpoint !== newSubscription?.endpoint
      ) {
        let subscribeResponse;
        let unsubscribeResponse;
        try {
          // Unsubscribe from old subscription
          unsubscribeResponse = await pushSubscriptionNotification(
            user?.userId,
            'unsubscribe',
            oldSubscription,
            true
          );
        } catch (err) {
          console.error(
            'pollForPushSubscriptionChange :: Error occurred while unsubscribing push subscriptions',
            err
          );
        }
        if (unsubscribeResponse) {
          logActivity({
            userId,
            expired: true,
            activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.PUSH_ACTIVITY,
            pushActivityType: PUSH_ACTIVITY_TYPES.PUSH_UNSUBSCRIBED,
            subsHash: unsubscribeResponse?.currentSubscription?.hash,
            subsEndpoint: oldSubscription?.endpoint,
            loginStatus: userLoggedIn,
            referer: window.location.href,
          });
        }

        window.localStorage.removeItem(currentSubscriptionLocalStorageKey);

        try {
          // Subscribe for new subscription
          subscribeResponse = await pushSubscriptionNotification(
            user?.userId,
            'subscribe',
            newSubscription
          );
        } catch (err) {
          console.error(
            'pollForPushSubscriptionChange :: Error occurred while subscribing subscriptions',
            err
          );
        }
        if (subscribeResponse) {
          logActivity({
            userId,
            activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.PUSH_ACTIVITY,
            pushActivityType: PUSH_ACTIVITY_TYPES.PUSH_UNSUBSCRIBED,
            subsHash: subscribeResponse?.currentSubscription?.hash,
            subsEndpoint: newSubscription?.endpoint,
            loginStatus: userLoggedIn,
            referer: window.location.href,
          });
        }
        window.localStorage.setItem(currentSubscriptionLocalStorageKey, newSubscription.toJSON());
      }
    }
  } catch (err) {
    console.log('Error has occurred while polling', err);
  }
};

export const startPollingForPushSubscriptionChange = (user) => {
  if (window.pushSubscriptionIntervalId) {
    // clear interval for previous cycle
    clearInterval(window.pushSubscriptionIntervalId);
  }
  pollForPushSubscriptionChange(user);
  // Poll every four hour
  window.pushSubscriptionIntervalId = setInterval(
    () => pollForPushSubscriptionChange(user),
    (PUSH_SUBSCRIPTION_POLLING_INTERVAL || 4) * 1000 * 3600
  );
};

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    if ('PushManager' in window) {
      return navigator.serviceWorker.register('/service-worker.js');
    } else {
      console.log('push manager is not supported');
    }
  } else {
    console.log("Browser doesn't support service worker");
  }
};

export const subscribeToPushNotifications = (pushNotificationVapidKey) => {
  return navigator.serviceWorker.ready
    .then((registration) => {
      return registration.pushManager
        .subscribe({
          userVisibleOnly: true,
          applicationServerKey: pushNotificationVapidKey,
        })
        .then((subscription) => {
          window.localStorage.setItem(currentSubscriptionLocalStorageKey, subscription.toJSON());
          return subscription;
        })
        .catch((err) => {
          throw err;
        });
    })
    .catch((error) => {
      throw error;
    });
};

/**
 * Get a new subcription from PushManager
 * @param {boolean} saveSubscription A boolean flag indicating whether or not we need to save subscription to localStorage
 * @returns {Promise<PushSubscription>}
 */
export const getSubscription = (saveSubscription = false) => {
  return new Promise((resolve, reject) => {
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        if (saveSubscription && !!subscription) {
          // save subscription to localStorage
          window.localStorage.setItem(currentSubscriptionLocalStorageKey, subscription.toJSON());
        }
        resolve(subscription);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const unSubscribeToPushNotifications = () => {
  return navigator.serviceWorker.ready
    .then((registration) => {
      return registration.pushManager
        .getSubscription(true)
        .then((subscription) => {
          if (subscription) {
            return subscription
              .unsubscribe()
              .then(() => subscription)
              .catch((err) => {
                throw err;
              });
          } else {
            throw err;
          }
        })
        .catch((err) => {
          throw err;
        });
    })
    .catch((err) => {
      throw err;
    });
};

export const pushSubscriptionNotification = (
  userId,
  action,
  subscription,
  expired,
  activityPayload
) => {
  const data = { userId, action, subscription, activityPayload };
  if (expired) data.expired = expired;
  return new Promise((resolve, reject) => {
    Service.put(HttpRoutes.pushSubscriptionNotification, data)
      .then((response) => resolve(response.data))
      .catch((err) => reject(err));
  });
};

export function shouldUserSubscribe() {
  const permission =
    window.Notification && typeof window.Notification !== 'undefined'
      ? window.Notification.permission
      : false;
  return permission === 'granted';
}

export function subscriberUserForPushNotifications(user, criteria = {}) {
  const userLoggedIn = user && user.isLoggedIn;
  const userId = user?.userId;
  getSubscription(true)
    .then((oldSubscription) => {
      if (!oldSubscription) {
        subscribeToPushNotifications(PUSH_NOTIFICATIONS_VAPID_KEY)
          .then((subscription) => {
            pushSubscriptionNotification(userId, 'subscribe', subscription)
              .then((response) => {
                // Add title and geo-location to userRating
                addTitleAndLocation(criteria);
                const subsHash = response?.currentSubscription?.hash;
                if (subsHash) {
                  logActivity({
                    activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.PUSH_ACTIVITY,
                    pushActivityType: PUSH_ACTIVITY_TYPES.PUSH_SUBSCRIBED,
                    userId,
                    subsHash,
                    subsEndpoint: subscription.endpoint,
                    loginStatus: userLoggedIn,
                    referer: window.location.href,
                  });
                }
              })
              .catch((err) => {
                console.error('Error occurred while subscribing notifications', err);
              });
          })
          .catch((err) => {
            console.log(
              'Error has occurred while subscribing the user from push notifications',
              err
            );
          });
      } else {
        window.localStorage.setItem(currentSubscriptionLocalStorageKey, oldSubscription.toJSON());
        pushSubscriptionNotification(userId, 'subscribe', oldSubscription)
          .then((response) => {
            // Add title and geo-location to userRating
            addTitleAndLocation(criteria);
            const subsHash = response?.currentSubscription?.hash;
            if (subsHash) {
              logActivity({
                activityType: USER_ACTIVITY_EVENTS.PUSH_NOTIFICATION.PUSH_ACTIVITY,
                pushActivityType: PUSH_ACTIVITY_TYPES.PUSH_SUBSCRIBED,
                userId,
                subsHash,
                subsEndpoint: oldSubscription.endpoint,
                loginStatus: userLoggedIn,
                referer: window.location.href,
              });
            }
          })
          .catch((err) => {
            console.error('Error occurred while subscribing notifications', err);
          });
      }
    })
    .catch((err) => {
      console.log('Error has occurred while getting user subscription', err);
    });
}

export const addTitleAndLocation = (criteria = {}) => {
  if (criteria.title) {
    // Add title to userRatings
    updateUserRating({ title: criteria.title });
  }
  if (criteria.geoState || criteria.geoCity) {
    // Add geo-location to userRatings
    updateUserRating({
      city: criteria.geoCity,
      stateAbbr: criteria.geoStateCode,
      state: criteria.geoState,
    });
  }
};
